import {
  useRef, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Carousel, Badge, GetRef } from 'antd';
import { SaveDiscardButtonsGroup } from 'components/styledComponents/modal/Modal.styled';
import { StyledCarouselWrapper } from 'components/styledComponents/slider/Slider.styled';
import {
  StyledNoteNoDataContent, StyledBadgeWrapper, StyledNotePagination, StyledNoteDataContent, StyledNotesModal, StyledPointer,
} from 'components/system/releaseNote/ReleaseNote.styled';
import noReleaseNoteChangesImg from 'assets/img/no_release_note_changes.png';
import { IconSvg } from 'shared/icon';
import { StyledButtonBlue, StyledButtonBlueUnbordered, StyledSmallButtonBlue } from 'components/styledComponents/button/Button.styled';
import { releaseNoteLabelsColor } from 'constants/mapping';
import { StyledIconDefault } from 'shared/icon/Icon.styled';
import { AdminEditorContainer } from 'shared/adminEditor';
import { AdminSelect } from 'shared/formControl';
import { StyledSelectOption } from 'components/styledComponents/select/Select.styled';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppSelector } from 'store/hooks';
import { makeSelectLocaleResource } from 'app/locale/selectors';
import enumTypes from 'constants/enumTypes';
import { useGetReleaseNoteQuery, useGetResourceReleaseNotesQuery, useMarkAsSeenReleaseNoteMutation } from 'features/releaseNote/api';

const firstChange = 1;

export interface ReleaseNoteModalProps {
  visible: boolean;
  closeModal: () => void;
  selected?: number;
  isView?: boolean;
}
type CarouselRef = GetRef<typeof Carousel>;

const selectLocale = makeSelectLocaleResource({ releaseNoteLabels: enumTypes.releaseNoteLabels });

const ReleaseNoteModal = ({
  visible, closeModal, selected, isView = false,
}: ReleaseNoteModalProps) => {
  const { t } = useTranslation();

  const [value, setValue] = useState<number>();
  const [current, setCurrent] = useState<number>(firstChange);
  const locale = useAppSelector(selectLocale);
  const { data: releaseNote } = useGetReleaseNoteQuery(value || skipToken);
  const { data: releaseNotes } = useGetResourceReleaseNotesQuery(undefined, { skip: !visible || isView });
  const [markAsSeenReleaseNote] = useMarkAsSeenReleaseNoteMutation();
  const { releaseNoteLabels } = locale;

  const carouselRef = useRef<CarouselRef>(null);

  useEffect(() => {
    const val = selected || releaseNotes?.[0]?.id;
    if (val) {
      setValue(val);
    }
  }, [releaseNotes, selected]);

  const renderNotes = () => releaseNote?.changes?.map(({ content, id }) => (
    <div key={id}>
      <AdminEditorContainer value={content} noToolbar readOnly />
    </div>
  ));

  const handleClose = () => {
    if (!isView && value) {
      markAsSeenReleaseNote(value);
    }

    setCurrent(firstChange);
    setValue(undefined);
    closeModal();
  };

  const getTotalDetails = () => <StyledNotePagination key={current}>{`${current}/${releaseNote?.changes?.length}`}</StyledNotePagination>;

  const handlePrev = useCallback(() => carouselRef?.current?.prev(), [carouselRef]);

  const handleNext = useCallback(() => carouselRef?.current?.next(), [carouselRef]);

  const keyup = useCallback(
    (e: KeyboardEvent): void => {
      if (carouselRef?.current) {
        if (e.keyCode === 37) {
          handlePrev();
        } else if (e.keyCode === 39) {
          handleNext();
        }
      }
    },
    [handleNext, handlePrev],
  );

  useEffect(() => {
    document.addEventListener('keyup', keyup);
    return () => {
      document.removeEventListener('keyup', keyup);
    };
  }, [keyup]);

  const handleAfterChange = (val: number) => setCurrent(val + 1);

  const getButtons = () => {
    const gotItButton = (
      <StyledButtonBlue key={0} htmlType="submit" size="middle" onClick={handleClose}>
        {t('system:gotIt')}
      </StyledButtonBlue>
    );

    if (!_.isEmpty(releaseNote) && _.isEmpty(releaseNote?.changes)) {
      return gotItButton;
    }

    const content = [];
    const count = releaseNote?.changes?.length ?? 0;

    if (current < count) {
      content.push(
        <StyledButtonBlueUnbordered key={1} type="default" size="middle" onClick={handleClose}>
          {t('system:skip')}
        </StyledButtonBlueUnbordered>,
      );
    }
    if (current > firstChange) {
      content.push(
        <StyledSmallButtonBlue key={2} htmlType="submit" size="middle" onClick={handlePrev}>
          {t('system:previous')}
        </StyledSmallButtonBlue>,
      );
    }
    if (current < count) {
      content.push(
        <StyledButtonBlue key={3} htmlType="submit" size="middle" onClick={handleNext}>
          {t('next')}
        </StyledButtonBlue>,
      );
    }
    if (current === count) {
      content.push(gotItButton);
    }

    return content;
  };

  const handleValueChange = (val: number) => {
    setValue(val);
    setCurrent(firstChange);
  };

  const isClosable = useMemo(() => (!isView && _.isEmpty(releaseNotes)) || (!_.isEmpty(releaseNote) && _.isEmpty(releaseNote?.changes)), [isView, releaseNotes, releaseNote]);

  const closeableProps = isView
    ? {
      onCancel: closeModal,
      closable: false,
    }
    : {
      closable: isClosable,
      onCancel: isClosable ? closeModal : undefined,
    };

  const releaseNotesOptions = releaseNotes?.map(({ isSeen, ...el }) => ({
    ...el,
    label: el.name,
    displayName: !isSeen ? (
      <StyledSelectOption>
        <StyledPointer />
        <span>{el.name}</span>
      </StyledSelectOption>
    ) : (
      el.name
    ),
  }));

  return (
    <StyledNotesModal
      getContainer=".ant-layout-content"
      width="49rem"
      height="50rem"
      open={visible}
      centered
      closeIcon={(
        <StyledIconDefault>
          <IconSvg icon="CloseModalIcon" height="0.86rem" width="0.86rem" />
        </StyledIconDefault>
      )}
      maskClosable={false}
      destroyOnHidden
      footer={(
        <SaveDiscardButtonsGroup>
          {!_.isEmpty(releaseNote?.changes) && getTotalDetails()}
          {getButtons()}
        </SaveDiscardButtonsGroup>
      )}
      {...closeableProps}
    >
      {!_.isEmpty(releaseNotes) && (
        <AdminSelect
          data={releaseNotesOptions}
          value={value}
          isToString={false}
          placeholder={t('select')}
          onChange={handleValueChange}
          allowClear={false}
          optionLabelProp="label"
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          showSearch={{
            filterOption: (input, option) => ((option?.label ?? '')?.toString() ?? '').toLowerCase().includes(input.toLowerCase()),
          }}
        />
      )}
      {(!_.isEmpty(releaseNote) && _.isEmpty(releaseNote?.changes)) || (!isView && _.isEmpty(releaseNotes)) ? (
        <StyledNoteNoDataContent>
          <img alt={t('system:noReleaseNote')} src={noReleaseNoteChangesImg} />
          <span>{t('system:whoops')}</span>
          <strong>{t('system:noContentMessage')}</strong>
        </StyledNoteNoDataContent>
      ) : (
        <StyledNoteDataContent>
          <StyledBadgeWrapper>
            <Badge.Ribbon text={releaseNote?.changes?.[current - 1]?.label ? releaseNoteLabels[releaseNote?.changes?.[current - 1]?.label] : undefined} color={releaseNote?.changes?.[current - 1]?.label ? releaseNoteLabelsColor[releaseNote?.changes?.[current - 1]?.label] : undefined} />
          </StyledBadgeWrapper>
          <StyledCarouselWrapper>
            <Carousel effect="fade" dots={false} afterChange={handleAfterChange} ref={carouselRef}>
              {renderNotes()}
            </Carousel>
          </StyledCarouselWrapper>
        </StyledNoteDataContent>
      )}
    </StyledNotesModal>
  );
};

export default ReleaseNoteModal;
